import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BlockIcon from '@mui/icons-material/Block';
import UndoIcon from '@mui/icons-material/Undo';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { updateMatter } from '../../actions/matters/updateMatter';
import { RAF_FIELD_SECTIONS, computeSectionCompletion, type FieldDef } from './raf-field-sections';

interface RafFieldsFormProps {
  matterId: string;
  extractedData: Record<string, unknown>;
  naSections: string[] | undefined;
  onSaved: () => void;
}

const RafFieldsForm: React.FC<RafFieldsFormProps> = ({ matterId, extractedData, naSections, onSaved }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>(() => ({ ...extractedData }));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [naUpdating, setNaUpdating] = useState(false);
  const naSet = useMemo(() => new Set(naSections ?? []), [naSections]);

  const dirtyCount = useMemo(() => {
    let count = 0;
    for (const key of Object.keys(formData)) {
      if (String(formData[key] ?? '') !== String(extractedData[key] ?? '')) count++;
    }
    return count;
  }, [formData, extractedData]);

  const handleFieldChange = useCallback((key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);
    try {
      await updateMatter(matterId, { rafExtractedJson: formData });
      setSaveSuccess(true);
      onSaved();
    } catch {
      setSaveError('Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const section = RAF_FIELD_SECTIONS[activeTab];
  const sectionIsNa = section ? naSet.has(section.id) : false;

  const handleToggleNa = async () => {
    if (!section) return;
    const next = sectionIsNa
      ? (naSections ?? []).filter((id) => id !== section.id)
      : [...(naSections ?? []), section.id];
    setNaUpdating(true);
    setSaveError('');
    try {
      await updateMatter(matterId, { naSections: next });
      onSaved();
    } catch {
      setSaveError('Failed to update N/A state.');
    } finally {
      setNaUpdating(false);
    }
  };

  // Per-section completion. Mutually-exclusive groups (e.g. unrepresented_yes/_no,
  // capacity_*, gender) collapse to ONE logical field via computeSectionCompletion.
  // N/A sections contribute zero filled but their total still counts toward the
  // denominator so toggling N/A doesn't shift the global percentage by chance.
  const sectionCompletions = useMemo(
    () => RAF_FIELD_SECTIONS.map((s) => computeSectionCompletion(s, formData, naSet.has(s.id))),
    [formData, naSet],
  );

  const totalFields = useMemo(
    () => sectionCompletions.reduce((sum, c) => sum + c.total, 0),
    [sectionCompletions],
  );
  const totalFilled = useMemo(
    () => sectionCompletions.reduce((sum, c) => sum + c.filled, 0),
    [sectionCompletions],
  );
  const filledPercent = totalFields > 0 ? Math.round((totalFilled / totalFields) * 100) : 0;
  const sectionsWithData = sectionCompletions.filter((c) => c.filled > 0).length;

  // SVG progress ring calculations
  const ringRadius = 20;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference - (filledPercent / 100) * ringCircumference;

  return (
    <Box>
      {/* Completion strip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          px: 2.5,
          background: 'linear-gradient(135deg, #1a2166 0%, #2a3480 100%)',
          borderRadius: '12px',
          color: '#fff',
        }}
      >
        {/* Progress ring */}
        <Box sx={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
          <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="22" cy="22" r={ringRadius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
            <circle
              cx="22" cy="22" r={ringRadius} fill="none"
              stroke="var(--green)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={ringCircumference}
              strokeDashoffset={ringOffset}
              style={{ transition: 'stroke-dashoffset 0.6s' }}
            />
          </svg>
          <Typography
            sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '0.68rem', fontWeight: 700, color: '#fff',
            }}
          >
            {filledPercent}%
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
            Fields extracted — review and edit
          </Typography>
          <Typography sx={{ fontSize: '0.74rem', opacity: 0.7, mt: 0.25 }}>
            {totalFilled} of {totalFields} fields populated · {sectionsWithData} section{sectionsWithData !== 1 ? 's' : ''} have data
          </Typography>
        </Box>
      </Box>

      {/* Section chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {RAF_FIELD_SECTIONS.map((s, i) => {
          const isActive = i === activeTab;
          const hasData = sectionCompletions[i].filled > 0;
          return (
            <Box
              key={s.id}
              onClick={() => setActiveTab(i)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.85,
                borderRadius: '20px',
                fontSize: '0.76rem',
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                border: '1.5px solid',
                borderColor: isActive ? 'var(--green)' : hasData ? 'var(--green-border)' : '#e0e4ec',
                color: isActive ? '#fff' : hasData ? 'var(--green-mid)' : 'var(--t2)',
                bgcolor: isActive ? 'var(--green)' : hasData ? '#f0faf4' : '#fff',
                boxShadow: isActive ? '0 2px 8px rgba(51,153,102,0.25)' : 'none',
                '&:hover': isActive
                  ? {}
                  : { borderColor: 'var(--green)', color: 'var(--green)' },
              }}
            >
              <Box component="span" sx={{ color: isActive ? '#fff' : 'inherit' }}>{s.label}</Box>
              {hasData && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    bgcolor: isActive ? 'rgba(255,255,255,0.25)' : '#d4eddf',
                    color: isActive ? '#fff' : 'var(--green)',
                    px: 0.75,
                    py: 0.1,
                    borderRadius: '8px',
                  }}
                >
                  {sectionCompletions[i].filled}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Fields card */}
      <Box
        sx={{
          bgcolor: '#fff',
          border: '1px solid #e0e4ec',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        }}
      >
        {/* Card header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.25,
            borderBottom: '1px solid #f0f2f5',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--t1)' }}>
              {section?.label}
            </Typography>
            {sectionIsNa && (
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: 'var(--t3)',
                  bgcolor: '#eef0f4',
                  px: 1,
                  py: 0.25,
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                N/A
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              disabled={naUpdating}
              onClick={handleToggleNa}
              startIcon={sectionIsNa ? <UndoIcon sx={{ fontSize: 14 }} /> : <BlockIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.72rem',
                color: sectionIsNa ? 'var(--t2)' : 'var(--err)',
                px: 1.25,
                py: 0.25,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
              }}
            >
              {sectionIsNa ? 'Undo Not Applicable' : 'Mark Not Applicable'}
            </Button>
            <Typography
              sx={{
                fontSize: '0.74rem',
                color: 'var(--green)',
                fontWeight: 600,
                bgcolor: 'var(--green-light)',
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
              }}
            >
              {sectionCompletions[activeTab].filled} of {sectionCompletions[activeTab].total} extracted
            </Typography>
          </Box>
        </Box>

        {/* Fields */}
        <Box sx={{ p: 3, maxHeight: 600, overflow: 'auto', opacity: sectionIsNa ? 0.45 : 1 }}>
          {sectionIsNa && (
            <Typography sx={{ fontSize: '0.8rem', color: 'var(--t3)', mb: 2, fontStyle: 'italic' }}>
              This section is marked Not Applicable. Its fields will be blanked when documents are generated.
            </Typography>
          )}
          {section && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              {section.fields.map((field) => (
                <FieldInput
                  key={field.key}
                  field={field}
                  value={formData[field.key]}
                  onChange={handleFieldChange}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Save bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.75,
            borderTop: '1px solid #f0f2f5',
          }}
        >
          {/* Left: navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                disabled={activeTab === 0}
                onClick={() => setActiveTab((t) => t - 1)}
                sx={{
                  border: '1.5px solid #e0e4ec',
                  borderRadius: '8px',
                  width: 34, height: 34,
                  color: 'var(--t2)',
                  '&:hover': { borderColor: 'var(--cyan)', color: 'var(--cyan)' },
                  '&.Mui-disabled': { color: 'var(--t4)', borderColor: '#eef0f4' },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={activeTab === RAF_FIELD_SECTIONS.length - 1}
                onClick={() => setActiveTab((t) => t + 1)}
                sx={{
                  border: '1.5px solid #e0e4ec',
                  borderRadius: '8px',
                  width: 34, height: 34,
                  color: 'var(--t2)',
                  '&:hover': { borderColor: 'var(--cyan)', color: 'var(--cyan)' },
                  '&.Mui-disabled': { color: 'var(--t4)', borderColor: '#eef0f4' },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography sx={{ fontSize: '0.76rem', color: 'var(--t3)' }}>
              {activeTab + 1} / {RAF_FIELD_SECTIONS.length}
            </Typography>

            {dirtyCount > 0 && (
              <Typography sx={{ color: 'var(--t3)', fontSize: '0.76rem', ml: 1 }}>
                {dirtyCount} field{dirtyCount !== 1 ? 's' : ''} modified
              </Typography>
            )}
            {saveSuccess && (
              <Typography sx={{ color: 'var(--ok)', fontSize: '0.76rem', fontWeight: 500, ml: 1 }}>
                Saved
              </Typography>
            )}
            {saveError && (
              <Typography sx={{ color: 'var(--err)', fontSize: '0.76rem', ml: 1 }}>{saveError}</Typography>
            )}
          </Box>

          {/* Right: save */}
          <Button
            variant="contained"
            size="small"
            disabled={dirtyCount === 0 || saving}
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: 'var(--green)',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.84rem',
              textTransform: 'none',
              borderRadius: '10px',
              px: 3.5,
              py: 1,
              boxShadow: '0 2px 12px rgba(51,153,102,0.2)',
              '&:hover': {
                bgcolor: 'var(--green-mid)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(51,153,102,0.3)',
              },
              '&.Mui-disabled': { bgcolor: 'var(--s3)', color: 'var(--t4)', boxShadow: 'none' },
            }}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// Individual field renderer
const FieldInput: React.FC<{
  field: FieldDef;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
}> = React.memo(({ field, value, onChange }) => {
  const strValue = value !== undefined && value !== null ? String(value) : '';
  const boolValue = value === true || value === 'true' || value === 'Yes' || value === 'yes';
  const hasValue = field.type === 'checkbox' ? boolValue : strValue.trim() !== '';

  if (field.type === 'checkbox') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={boolValue}
            onChange={(e) => onChange(field.key, e.target.checked)}
            size="small"
            sx={{ color: 'var(--t3)', '&.Mui-checked': { color: 'var(--green)' } }}
          />
        }
        label={
          <Typography sx={{ fontSize: '0.84rem', color: 'var(--t2)' }}>{field.label}</Typography>
        }
        sx={{
          mx: 0,
          px: 2,
          py: 1,
          bgcolor: '#f8fafb',
          borderRadius: '10px',
          border: '1px solid #eef0f4',
        }}
      />
    );
  }

  return (
    <TextField
      label={field.label}
      value={strValue}
      onChange={(e) => onChange(field.key, e.target.value)}
      size="small"
      fullWidth
      type={field.type === 'currency' ? 'number' : 'text'}
      placeholder={hasValue ? undefined : 'Not extracted — enter manually'}
      slotProps={{
        inputLabel: {
          sx: {
            color: 'var(--t3)',
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            '&::before': hasValue
              ? {
                  content: '""',
                  display: 'inline-block',
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: 'var(--green)',
                  flexShrink: 0,
                }
              : {},
          },
        },
        input: {
          sx: {
            color: 'var(--t1)',
            fontSize: '0.88rem',
            fontWeight: 500,
            bgcolor: '#ffffff',
            borderRadius: '10px',
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e8eaef',
            borderWidth: '1.5px',
            ...(hasValue && {
              borderBottomWidth: '2px',
              borderBottomColor: 'var(--green)',
            }),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--green)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--green)',
            boxShadow: '0 0 0 4px rgba(51,153,102,0.06), 0 2px 8px rgba(51,153,102,0.08)',
          },
        },
      }}
    />
  );
});

export default RafFieldsForm;
